import ProposalReviewer from "work/proposals/reviewers/proposal-reviewer";

const SAVE_UPDATES_LABEL = "Save Updates";
const SEND_TO_REVIEWER_LABEL = "Send to Reviewer";
const SEND_TO_REVIEWERS_LABEL = "Send to Reviewers";
const UPDATE_REVIEWER_LABEL = "Update Reviewer";
const UPDATE_REVIEWERS_LABEL = "Update Reviewers";
const REMOVE_REVIEWER_LABEL = "Remove Reviewer";
const REMOVE_REVIEWERS_LABEL = "Remove Reviewers";
const REVOKE_EDIT_RIGHTS_LABEL = "Revoke Edit Rights";
const NO_UPDATES_LABEL = "No Updates to Save";
const SEND_FOR_REVIEW_LABEL = "Send for review";
const SAVE_REVIEWER_CHANGES_LABEL = "Save changes";
const UNCLASSIFIED_REVIEWER_CHANGES_SUMMARY = "Unsaved reviewer changes";

const CONFIRM_OK_BUTTON_TEXT_BY_LABEL = new Map([
  [SAVE_UPDATES_LABEL, "Save"],
  [SEND_TO_REVIEWER_LABEL, "Send"],
  [SEND_TO_REVIEWERS_LABEL, "Send"],
  [REMOVE_REVIEWER_LABEL, "Remove"],
  [REMOVE_REVIEWERS_LABEL, "Remove"],
  [REVOKE_EDIT_RIGHTS_LABEL, "Revoke"],
]);

export type SendButtonLabelArgs = {
  addedReviewersCount?: number;
  awaitingRequestCount?: number;
  changedReviewersCount?: number;
  editRightsGrantedCount?: number;
  editRightsRevokedCount?: number;
  removedReviewersCount?: number;
  selectedReviewersCount?: number;
  updatedReviewersCount?: number;
  proposalIsModifiedByBuilder: boolean;
  proposalHasUnsavedRedlineChanges: boolean;
};

export type ReviewerChangeSummary = {
  addedEditingReviewersCount: number;
  addedReviewersCount: number;
  /**
   * Reviewers already on the roster whom nobody has asked yet -- the ones a revision of a hired
   * agreement carried over from the agreement it revises. They are not an edit to the roster, so the
   * before/after diff above cannot see them, and without counting them the Send control read "No
   * updates" over a team that had never been told the revision existed.
   */
  awaitingRequestCount: number;
  changedReviewersCount: number;
  editRightsGrantedCount: number;
  editRightsRevokedCount: number;
  removedReviewersCount: number;
  selectedReviewersCount: number;
  updatedReviewersCount: number;
  hasReviewerChanges: boolean;
};

export type ReviewerActionStateArgs = {
  originalReviewers?: ProposalReviewer[];
  selectedReviewers?: ProposalReviewer[];
  proposalIsModifiedByBuilder: boolean;
  proposalHasUnsavedRedlineChanges: boolean;
  reviewersAreModified?: boolean;
};

export type ReviewerSendActionStateArgs = {
  originalReviewers?: ProposalReviewer[];
  selectedReviewers?: ProposalReviewer[];
  /** The host page's unsaved-changes signal (`isDirty` on both proposal pages). */
  proposalHasUnsavedChanges: boolean;
};

export type ReviewerSendActionState = {
  /** Reviewers the press would ask to review: added this session, or carried over and never asked. */
  addedReviewersCount: number;
  /**
   * Whether pressing the popover's commit control has unsaved proposal content riding along with it,
   * and so must ask before it goes (#5134). The control is available either way — this only decides
   * whether a confirmation stands between the press and the save.
   *
   * The popover says nothing about it itself. The confirmation the page opens lists exactly what the
   * save will carry, which a line of footer text could only guess at: both pages count the roster edit
   * itself as unsaved, so the popover cannot tell "and your other edits" from "and nothing else".
   */
  confirmsUnsavedProposalChanges: boolean;
  /**
   * Whether the roster differs from what the server last acknowledged at all. The popover's footer
   * exists only while this is true, so there is never a disabled commit button to explain.
   */
  hasChanges: boolean;
  label: string;
  /** What the press would do, as short counted clauses ("2 to notify · 1 to remove"); empty when unchanged. */
  summary: string;
};

export type ReviewerActionState = ReviewerChangeSummary & {
  label: string;
  disabled: boolean;
  hasPendingSaveUpdates: boolean;
  confirmTitle: string;
  confirmMessage: string;
  confirmSupplementalMessage?: string;
  confirmOkButtonText: string;
};

type ReviewerSelectionArgs = {
  selectedReviewers: ProposalReviewer[];
  reviewer: ProposalReviewer;
};

type ReviewerChange = {
  originalReviewer: ProposalReviewer;
  reviewer: ProposalReviewer;
};

function getSelectedReviewer(
  selectedReviewers: ProposalReviewer[],
  reviewer: ProposalReviewer,
): ProposalReviewer | undefined {
  return selectedReviewers.find((currentReviewer) => currentReviewer.userId.isEqualTo(reviewer.userId));
}

export function toggleReviewerSelection(args: ReviewerSelectionArgs): ProposalReviewer[] {
  const { selectedReviewers, reviewer } = args;
  const selectedReviewer = getSelectedReviewer(selectedReviewers, reviewer);

  if (selectedReviewer) {
    return selectedReviewers.filter((currentReviewer) => !currentReviewer.userId.isEqualTo(reviewer.userId));
  }

  return [...selectedReviewers, reviewer];
}

/**
 * Drops a reviewer from the selection outright (#5134's trash can). Not `toggleReviewerSelection`:
 * a toggle pressed against somebody who is already gone silently re-adds them, which is the opposite
 * of what a delete control promises. Removing somebody who is not selected is a no-op.
 */
export function removeReviewer(args: ReviewerSelectionArgs): ProposalReviewer[] {
  const { selectedReviewers, reviewer } = args;

  return selectedReviewers.filter((currentReviewer) => !currentReviewer.userId.isEqualTo(reviewer.userId));
}

/**
 * Everybody the next Send asks to review: a reviewer added this session, and a reviewer carried on to
 * a revision whom nobody has asked yet (#7083). Both are sent the request with their note, so both
 * can be given one.
 */
export function getReviewRequestRecipientUserIds(args: {
  originalReviewers: ProposalReviewer[];
  selectedReviewers: ProposalReviewer[];
}): Set<string> {
  return new Set(
    getReviewRequestRecipients(args.originalReviewers, args.selectedReviewers).map((reviewer) => reviewer.userId.value),
  );
}

/**
 * The note most of the reviewers about to be asked share, which the popover presents as the one note
 * sent with every request. Nothing stores it: the contract carries a message per reviewer, so the
 * shared note is recovered from those messages when the popover opens — after a reload, the staged
 * selection comes back with its messages and the note is read off them again.
 *
 * Ties go to the reviewer listed first, and a session with nobody to ask, or nobody with a message,
 * has no shared note.
 */
export function inferSharedReviewerNote(args: {
  originalReviewers: ProposalReviewer[];
  selectedReviewers: ProposalReviewer[];
}): string {
  const countsByNote = new Map<string, number>();
  getReviewRequestRecipients(args.originalReviewers, args.selectedReviewers).forEach((reviewer) => {
    const note = reviewer.customMessage?.trim() ?? "";
    if (note) countsByNote.set(note, (countsByNote.get(note) ?? 0) + 1);
  });

  let sharedNote = "";
  let sharedNoteCount = 0;
  countsByNote.forEach((count, note) => {
    if (count > sharedNoteCount) {
      sharedNote = note;
      sharedNoteCount = count;
    }
  });

  return sharedNote;
}

/**
 * Whether a reviewer about to be asked receives the shared note rather than a separate one of their
 * own. A reviewer with no message follows it too: that is where one lands who was added before any
 * note was written.
 */
export function followsSharedReviewerNote(reviewer: ProposalReviewer, sharedNote: string): boolean {
  const note = reviewer.customMessage?.trim() ?? "";
  return note === "" || note === sharedNote.trim();
}

/**
 * Rewrites the shared note on every reviewer about to be asked who follows it, leaving a reviewer with
 * a separate note alone. A reviewer who has already been asked is not touched: they were notified with
 * whatever note they were sent, and changing it now would tell nobody.
 */
export function applySharedReviewerNote(args: {
  originalReviewers: ProposalReviewer[];
  selectedReviewers: ProposalReviewer[];
  previousNote: string;
  nextNote: string;
}): ProposalReviewer[] {
  const { originalReviewers, selectedReviewers, previousNote } = args;
  const nextNote = args.nextNote.trim();
  const recipientUserIds = getReviewRequestRecipientUserIds({ originalReviewers, selectedReviewers });

  return selectedReviewers.map((reviewer) => {
    if (!recipientUserIds.has(reviewer.userId.value) || !followsSharedReviewerNote(reviewer, previousNote)) {
      return reviewer;
    }

    return setReviewerNote(reviewer, nextNote);
  });
}

/** The same reviewer carrying a different note; an empty note is stored as no note at all. */
export function setReviewerNote(reviewer: ProposalReviewer, note: string | undefined): ProposalReviewer {
  return new ProposalReviewer({
    entityMember: reviewer.entityMember,
    isEditing: reviewer.isEditing,
    dateApproved: reviewer.dateApproved,
    customMessage: note?.trim() || undefined,
    isAwaitingReviewRequest: reviewer.isAwaitingReviewRequest,
  });
}

function getReviewRequestRecipients(
  originalReviewers: ProposalReviewer[],
  selectedReviewers: ProposalReviewer[],
): ProposalReviewer[] {
  const originalUserIds = new Set(originalReviewers.map((reviewer) => reviewer.userId.value));
  return selectedReviewers.filter(
    (reviewer) => !originalUserIds.has(reviewer.userId.value) || reviewer.isAwaitingReviewRequest,
  );
}

/**
 * A reviewer not yet asked whose note is all that changed. The note goes out with the request, so it
 * is part of asking them, not a separate update to somebody already asked.
 */
function isNoteForPendingRequest(reviewerChange: ReviewerChange): boolean {
  const { originalReviewer, reviewer } = reviewerChange;
  return (
    reviewer.isAwaitingReviewRequest &&
    originalReviewer.entityMember.isEqualTo(reviewer.entityMember) &&
    originalReviewer.isEditing === reviewer.isEditing
  );
}

export function toggleReviewerEditing(args: ReviewerSelectionArgs): ProposalReviewer[] {
  const { selectedReviewers, reviewer } = args;
  const selectedReviewer = getSelectedReviewer(selectedReviewers, reviewer);

  if (!selectedReviewer) {
    const updatedReviewers = [
      ...selectedReviewers,
      new ProposalReviewer({
        entityMember: reviewer.entityMember,
        isEditing: true,
        dateApproved: reviewer.dateApproved,
        customMessage: reviewer.customMessage,
      }),
    ];

    return updatedReviewers;
  }

  const updatedReviewers = selectedReviewers.map((currentReviewer) => {
    if (!currentReviewer.userId.isEqualTo(reviewer.userId)) {
      return currentReviewer;
    }

    return new ProposalReviewer({
      entityMember: selectedReviewer.entityMember,
      isEditing: !selectedReviewer.isEditing,
      dateApproved: selectedReviewer.dateApproved,
      customMessage: selectedReviewer.customMessage,
    });
  });

  return updatedReviewers;
}

export function getReviewerChangeSummary(args: {
  originalReviewers?: ProposalReviewer[];
  selectedReviewers?: ProposalReviewer[];
}): ReviewerChangeSummary {
  const originalReviewers = args.originalReviewers ?? [];
  const selectedReviewers = args.selectedReviewers ?? [];
  const originalReviewersByUserId = getReviewersByUserId(originalReviewers);
  const selectedReviewersByUserId = getReviewersByUserId(selectedReviewers);
  const addedReviewers = selectedReviewers.filter(
    (reviewer) => !originalReviewersByUserId.has(reviewer.userId.value),
  );
  const addedReviewersCount = addedReviewers.length;
  const addedEditingReviewersCount = addedReviewers.filter((reviewer) => reviewer.isEditing).length;
  const removedReviewersCount = originalReviewers.filter(
    (originalReviewer) => !selectedReviewersByUserId.has(originalReviewer.userId.value),
  ).length;
  const changedReviewers = selectedReviewers
    .map((reviewer) => ({
      reviewer,
      originalReviewer: originalReviewersByUserId.get(reviewer.userId.value),
    }))
    .filter(
      (reviewerChange): reviewerChange is ReviewerChange =>
        Boolean(reviewerChange.originalReviewer && !reviewerChange.originalReviewer.isEqualTo(reviewerChange.reviewer)),
    )
    .filter((reviewerChange) => !isNoteForPendingRequest(reviewerChange));
  const editRightsGrantedCount = changedReviewers.filter(
    ({ originalReviewer, reviewer }) => !originalReviewer.isEditing && reviewer.isEditing,
  ).length;
  const editRightsRevokedCount = changedReviewers.filter(
    ({ originalReviewer, reviewer }) => originalReviewer.isEditing && !reviewer.isEditing,
  ).length;
  const updatedReviewersCount = changedReviewers.filter(({ originalReviewer, reviewer }) => {
    return (
      !originalReviewer.entityMember.isEqualTo(reviewer.entityMember) ||
      (originalReviewer.customMessage ?? "") !== (reviewer.customMessage ?? "")
    );
  }).length;
  const awaitingRequestCount = selectedReviewers.filter(
    (reviewer) => reviewer.isAwaitingReviewRequest && originalReviewersByUserId.has(reviewer.userId.value),
  ).length;

  return {
    addedEditingReviewersCount,
    addedReviewersCount,
    awaitingRequestCount,
    changedReviewersCount: changedReviewers.length,
    editRightsGrantedCount,
    editRightsRevokedCount,
    removedReviewersCount,
    selectedReviewersCount: selectedReviewers.length,
    updatedReviewersCount,
    hasReviewerChanges:
      addedReviewersCount > 0 ||
      removedReviewersCount > 0 ||
      changedReviewers.length > 0 ||
      awaitingRequestCount > 0,
  };
}

function getReviewersByUserId(reviewers: ProposalReviewer[]): Map<string, ProposalReviewer> {
  const reviewersByUserId = new Map<string, ProposalReviewer>();

  reviewers.forEach((reviewer) => {
    if (!reviewersByUserId.has(reviewer.userId.value)) {
      reviewersByUserId.set(reviewer.userId.value, reviewer);
    }
  });

  return reviewersByUserId;
}

export function getSendButtonLabel(args: SendButtonLabelArgs): string {
  const {
    addedReviewersCount = 0,
    awaitingRequestCount = 0,
    changedReviewersCount = 0,
    editRightsGrantedCount = 0,
    editRightsRevokedCount = 0,
    removedReviewersCount = 0,
    selectedReviewersCount = 0,
    updatedReviewersCount = 0,
    proposalIsModifiedByBuilder,
    proposalHasUnsavedRedlineChanges,
  } = args;
  // Comments used to count as pending work here, so a reviewer holding one saw a "Save Updates" button
  // over a proposal they had not touched. A comment is written when it is written; the reviewer's button
  // speaks for their proposal changes.
  const hasPendingChanges = proposalIsModifiedByBuilder || proposalHasUnsavedRedlineChanges;
  const hasReviewerChanges =
    addedReviewersCount > 0 || removedReviewersCount > 0 || changedReviewersCount > 0 || awaitingRequestCount > 0;
  const hasSendToReviewerChanges =
    addedReviewersCount > 0 || awaitingRequestCount > 0 || editRightsGrantedCount > 0;
  const hasRemovalChanges = removedReviewersCount > 0;
  const hasRevokeEditRightsChanges = editRightsRevokedCount > 0;
  const hasUnclassifiedReviewerChanges =
    changedReviewersCount > 0 &&
    editRightsGrantedCount === 0 &&
    editRightsRevokedCount === 0 &&
    updatedReviewersCount === 0;
  const hasReviewerUpdates = updatedReviewersCount > 0 || hasUnclassifiedReviewerChanges;
  const reviewerChangeTypesCount = [
    hasSendToReviewerChanges,
    hasRemovalChanges,
    hasRevokeEditRightsChanges,
    hasReviewerUpdates,
  ].filter(Boolean).length;

  if (hasPendingChanges && !hasReviewerChanges) {
    return SAVE_UPDATES_LABEL;
  }
  if ((hasPendingChanges && hasReviewerChanges) || reviewerChangeTypesCount > 1 || hasReviewerUpdates) {
    return getUpdateReviewersLabel(selectedReviewersCount);
  }
  if (hasSendToReviewerChanges) {
    return getSendToReviewersLabel(addedReviewersCount + awaitingRequestCount + editRightsGrantedCount);
  }
  if (hasRemovalChanges) {
    return getRemoveReviewersLabel(removedReviewersCount);
  }
  if (hasRevokeEditRightsChanges) {
    return REVOKE_EDIT_RIGHTS_LABEL;
  }

  return NO_UPDATES_LABEL;
}

function getSendToReviewersLabel(reviewersCount: number): string {
  return reviewersCount === 1 ? SEND_TO_REVIEWER_LABEL : SEND_TO_REVIEWERS_LABEL;
}

function getUpdateReviewersLabel(reviewersCount: number): string {
  return reviewersCount === 1 ? UPDATE_REVIEWER_LABEL : UPDATE_REVIEWERS_LABEL;
}

function getRemoveReviewersLabel(reviewersCount: number): string {
  return reviewersCount === 1 ? REMOVE_REVIEWER_LABEL : REMOVE_REVIEWERS_LABEL;
}

/**
 * The reviewers popover's own Send control (#4842). It is not another spelling of the page's
 * label-morphing Save — that button lives in the page header and is deliberately disabled while this
 * popover is open (`isManagingReviewers`), which is exactly why a reviewer added here had no way to be
 * sent without first closing the popover.
 *
 * It exists only while the roster differs from what the server last acknowledged, and it commits
 * every kind of roster edit, not only additions. It used to answer one question — has this session
 * ADDED somebody? — and sat disabled over a removal or a revoked edit right, telling the actor to
 * "add a reviewer" when what they had to do was close the popover and find the page's Save. The label
 * says which kind of press it is: "Send for review" when somebody will be asked to review, "Save
 * changes" when the edit only rearranges who already has been.
 *
 * #5134: the host page's `isDirty` used to be a second, independent requirement, which made Send the
 * page Save wearing a different label — an added reviewer restored from the durable staging store after
 * a reload sat behind a permanently disabled button reading "There are no unsaved proposal changes to
 * send", with no way forward but to touch the proposal. Unsaved proposal content does not gate the
 * control; it decides whether the press is confirmed first (`confirmsUnsavedProposalChanges`), because
 * a press that also flushes edits the actor did not ask to save is worth one question.
 */
export function getReviewerSendActionState(args: ReviewerSendActionStateArgs): ReviewerSendActionState {
  const changes = getReviewerChangeSummary({
    originalReviewers: args.originalReviewers,
    selectedReviewers: args.selectedReviewers,
  });
  // A reviewer carried on to a revision is already with the server and so is not an addition, but
  // nobody has asked them anything. There is as much to send as for a reviewer added this session.
  const sendableReviewersCount = changes.addedReviewersCount + changes.awaitingRequestCount;
  const hasChanges = changes.hasReviewerChanges;
  const confirmsUnsavedProposalChanges = hasChanges && args.proposalHasUnsavedChanges;

  return {
    addedReviewersCount: sendableReviewersCount,
    confirmsUnsavedProposalChanges,
    hasChanges,
    label: sendableReviewersCount > 0 ? SEND_FOR_REVIEW_LABEL : SAVE_REVIEWER_CHANGES_LABEL,
    summary: hasChanges ? summarizeReviewerChanges(changes, sendableReviewersCount) : "",
  };
}

function describeAccess(reviewer: ProposalReviewer): string {
  return reviewer.isEditing ? "Can edit" : "Can comment";
}

/**
 * The footer summary spelled out one reviewer at a time ("Add Robin Reviewer · Can comment", "Remove
 * Kit Reviewer"), in the summary's own order: who will be asked, who comes off, whose access moves.
 * The counts fit on one line; this is what they are counts of, for the summary's tooltip.
 */
export function describeReviewerChanges(args: {
  originalReviewers: ProposalReviewer[];
  selectedReviewers: ProposalReviewer[];
  getName: (reviewer: ProposalReviewer) => string;
}): string[] {
  const { originalReviewers, selectedReviewers, getName } = args;
  const originalReviewersByUserId = getReviewersByUserId(originalReviewers);
  const selectedReviewersByUserId = getReviewersByUserId(selectedReviewers);
  const toNotify: string[] = [];
  const toRemove: string[] = [];
  const toChange: string[] = [];

  selectedReviewers.forEach((reviewer) => {
    const originalReviewer = originalReviewersByUserId.get(reviewer.userId.value);
    const name = getName(reviewer);

    if (!originalReviewer) {
      toNotify.push(`Add ${name} · ${describeAccess(reviewer)}`);
      return;
    }
    if (reviewer.isAwaitingReviewRequest) toNotify.push(`Ask ${name} to review`);
    if (originalReviewer.isEditing !== reviewer.isEditing) {
      toChange.push(`${name}: ${describeAccess(originalReviewer)} → ${describeAccess(reviewer)}`);
    } else if (!originalReviewer.isEqualTo(reviewer) && !isNoteForPendingRequest({ originalReviewer, reviewer })) {
      toChange.push(`Update ${name}`);
    }
  });
  originalReviewers
    .filter((reviewer) => !selectedReviewersByUserId.has(reviewer.userId.value))
    .forEach((reviewer) => toRemove.push(`Remove ${getName(reviewer)}`));

  return [...toNotify, ...toRemove, ...toChange];
}

function summarizeReviewerChanges(changes: ReviewerChangeSummary, sendableReviewersCount: number): string {
  const accessChangesCount = changes.editRightsGrantedCount + changes.editRightsRevokedCount;
  const clauses = [
    sendableReviewersCount > 0 ? `${sendableReviewersCount} to notify` : undefined,
    changes.removedReviewersCount > 0 ? `${changes.removedReviewersCount} to remove` : undefined,
    accessChangesCount > 0 ? `${accessChangesCount} access ${accessChangesCount === 1 ? "change" : "changes"}` : undefined,
  ].filter((clause): clause is string => clause !== undefined);

  return clauses.length > 0 ? clauses.join(" · ") : UNCLASSIFIED_REVIEWER_CHANGES_SUMMARY;
}

export function getReviewerActionState(args: ReviewerActionStateArgs): ReviewerActionState {
  const {
    proposalIsModifiedByBuilder,
    proposalHasUnsavedRedlineChanges,
    reviewersAreModified = false,
  } = args;
  const reviewerChanges = getReviewerChangeSummary({
    originalReviewers: args.originalReviewers,
    selectedReviewers: args.selectedReviewers,
  });
  const changedReviewersCount =
    reviewerChanges.changedReviewersCount || (reviewersAreModified && !reviewerChanges.hasReviewerChanges ? 1 : 0);
  const label = getSendButtonLabel({
    addedReviewersCount: reviewerChanges.addedReviewersCount,
    awaitingRequestCount: reviewerChanges.awaitingRequestCount,
    changedReviewersCount,
    editRightsGrantedCount: reviewerChanges.editRightsGrantedCount,
    editRightsRevokedCount: reviewerChanges.editRightsRevokedCount,
    removedReviewersCount: reviewerChanges.removedReviewersCount,
    selectedReviewersCount: reviewerChanges.selectedReviewersCount,
    updatedReviewersCount: reviewerChanges.updatedReviewersCount,
    proposalIsModifiedByBuilder,
    proposalHasUnsavedRedlineChanges,
  });
  const hasReviewerChanges = reviewerChanges.hasReviewerChanges || reviewersAreModified;
  const hasPendingSaveUpdates = proposalIsModifiedByBuilder || proposalHasUnsavedRedlineChanges;
  return {
    ...reviewerChanges,
    changedReviewersCount,
    hasReviewerChanges,
    label,
    disabled: !hasReviewerChanges && !hasPendingSaveUpdates,
    hasPendingSaveUpdates,
    confirmTitle: label === SAVE_UPDATES_LABEL ? "Save Updates?" : `${label}?`,
    confirmMessage: hasPendingSaveUpdates
      ? "This will save all pending changes for review."
      : "This will update the proposal reviewers.",
    confirmSupplementalMessage: undefined,
    confirmOkButtonText: getConfirmOkButtonText(label),
  };
}

function getConfirmOkButtonText(label: string) {
  return CONFIRM_OK_BUTTON_TEXT_BY_LABEL.get(label) ?? "Update";
}
